import { useState, useRef, useEffect } from "react";
import { Mic, Square, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VoiceRecorder({ onTranscript, disabled }) {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };

      mediaRecorder.start();
      setRecording(true);
      setDuration(0);
      setTranscript("");
      setAudioBlob(null);

      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    } catch (e) {
      toast.error("Microphone access denied. Please allow microphone access.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");
      const res = await axios.post(`${API}/transcribe`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = res.data.text;
      if (text && text.trim()) {
        setTranscript(text.trim());
      } else {
        toast.error("Could not detect any speech. Try again.");
      }
    } catch (e) {
      toast.error("Transcription failed. Please try again.");
    } finally {
      setTranscribing(false);
    }
  };

  const sendTranscript = () => {
    if (transcript.trim()) {
      onTranscript(transcript.trim());
      reset();
    }
  };

  const reset = () => {
    setAudioBlob(null);
    setTranscript("");
    setDuration(0);
    setRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatDuration = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  // State: idle (show mic button)
  if (!recording && !audioBlob && !transcript) {
    return (
      <Button
        data-testid="voice-record-btn"
        onClick={startRecording}
        disabled={disabled}
        variant="outline"
        className="border-stone-200 hover:bg-stone-50 hover:border-stone-400 transition-all duration-200 rounded-xl px-3 self-end"
        title="Record voice message"
      >
        <Mic className="w-4 h-4 text-stone-600" />
      </Button>
    );
  }

  // State: recording
  if (recording) {
    return (
      <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 animate-fade-in self-end" data-testid="voice-recording-indicator">
        <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
        <span className="text-sm font-medium text-red-700">{formatDuration(duration)}</span>
        <Button
          data-testid="voice-stop-btn"
          onClick={stopRecording}
          size="sm"
          className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1 h-auto"
        >
          <Square className="w-3 h-3 mr-1.5" /> Stop
        </Button>
      </div>
    );
  }

  // State: recorded, needs transcription or showing transcript
  if (audioBlob && !transcript) {
    return (
      <div className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5 animate-fade-in self-end" data-testid="voice-review-panel">
        <span className="text-sm text-stone-600">{formatDuration(duration)} recorded</span>
        {transcribing ? (
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Transcribing...
          </div>
        ) : (
          <>
            <Button
              data-testid="voice-transcribe-btn"
              onClick={transcribeAudio}
              size="sm"
              className="bg-stone-900 hover:bg-stone-800 text-stone-50 rounded-lg px-3 py-1 h-auto text-xs"
            >
              Transcribe
            </Button>
            <Button
              data-testid="voice-discard-btn"
              onClick={reset}
              size="sm"
              variant="ghost"
              className="text-stone-400 hover:text-stone-600 rounded-lg px-2 py-1 h-auto"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>
    );
  }

  // State: transcript ready for review
  if (transcript) {
    return (
      <div className="flex-1 flex flex-col gap-2 animate-fade-in" data-testid="voice-transcript-review">
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-xs text-amber-600 font-medium mb-1 uppercase tracking-wide">Voice transcript — review before sending</p>
          <p className="text-sm text-stone-800 leading-relaxed">{transcript}</p>
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            data-testid="voice-discard-transcript-btn"
            onClick={reset}
            size="sm"
            variant="outline"
            className="text-xs border-stone-200 hover:bg-stone-50 rounded-lg"
          >
            <X className="w-3 h-3 mr-1" /> Discard
          </Button>
          <Button
            data-testid="voice-send-btn"
            onClick={sendTranscript}
            size="sm"
            className="bg-stone-900 hover:bg-stone-800 text-stone-50 rounded-lg text-xs"
          >
            <Send className="w-3 h-3 mr-1" /> Send
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
